import { atom } from "jotai"
import { atomFamily } from "jotai/utils"

import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import { DataWithId } from "#customTypes/types"

import { createLoadingStateAtoms } from "./loadingStateAtoms"

/**
 * Generic factory for creating Jotai atoms for Firebase document collections.
 * This provides a consistent state management pattern across all doc types.
 *
 * Architecture:
 * - docByIdAtom: atomFamily for individual documents (keyed by ID)
 * - allDocsAtom: atom for the full collection
 * - docsMapAtom: derived atom providing a Map<id, doc> for fast lookups
 * - filteredDocsAtom: atomFamily for query results (keyed by stringified query config)
 */
export function createDocAtoms<T extends DataWithId>(docType: DOC_TYPES) {
  const logPrefix = `[Jotai:${docType}]`

  // Create loading state atoms for this doc type
  const loadingAtoms = createLoadingStateAtoms(docType)

  /**
   * ATOM FAMILY: Individual documents by ID
   * Each ID gets its own atom. When a doc is updated via subscription,
   * only components using that specific ID will re-render.
   *
   * THIS IS THE SINGLE SOURCE OF TRUTH for all documents of this type,
   * regardless of how they were fetched (by ID, query, batch, etc.)
   */
  const docByIdAtom = atomFamily((id: Uuid | null | undefined) => {
    const baseAtom = atom<T | null>(null)
    baseAtom.debugLabel = `${docType}:doc:${id}`

    // console.log(`${logPrefix} 🏗️ Created docByIdAtom for ID: ${id}`)
    return baseAtom
  })

  /**
   * ATOM: Master list of all loaded document IDs
   * Tracks ALL IDs that have been loaded for this doc type, regardless of fetch method.
   * This is the single source of truth for "what IDs do we have loaded?"
   * All fetch methods (useOneDoc, useSomeDocs, useAllDocs, useFilteredDocs) update this.
   */
  const allLoadedIdsAtom = atom<Set<Uuid>>(new Set<Uuid>())
  allLoadedIdsAtom.debugLabel = `${docType}:allLoadedIds`

  /**
   * ATOM: All document IDs in the collection
   * Stores ONLY IDs, not full documents. This is the list of IDs from useAllDocs.
   * This is a subset of allLoadedIdsAtom (only IDs from the "all docs" subscription).
   */
  const allDocIdsAtom = atom<Uuid[]>([])
  allDocIdsAtom.debugLabel = `${docType}:allIds`

  /**
   * DERIVED ATOM: All documents (maps IDs to actual docs)
   * Reads from allDocIdsAtom and maps each ID to its document via docByIdAtom
   */
  const allDocsAtom = atom((get) => {
    const ids = get(allDocIdsAtom)
    return ids
      .map((id) => get(docByIdAtom(id)))
      .filter((doc): doc is T => doc !== null)
  })
  allDocsAtom.debugLabel = `${docType}:all`

  /**
   * ATOM FAMILY: Batch of doc IDs by query key
   * Each unique set of IDs gets its own atom that stores ONLY IDs
   */
  const docIdsForBatchAtom = atomFamily((idsKey: string) => {
    const baseAtom = atom<Uuid[]>([])
    baseAtom.debugLabel = `${docType}:batchIds:${idsKey.slice(0, 50)}`

    // console.log(
    //   `${logPrefix} 📦 Created docIdsForBatchAtom for IDs: ${idsKey.slice(0, 100)}`
    // )
    return baseAtom
  })

  /**
   * DERIVED ATOM FAMILY: Batch of docs by IDs (maps IDs to actual docs)
   * Reads from docIdsForBatchAtom and maps each ID to its document
   */
  const docsBatchAtom = atomFamily((idsKey: string) => {
    return atom((get) => {
      const ids = get(docIdsForBatchAtom(idsKey))
      return ids
        .map((id) => get(docByIdAtom(id)))
        .filter((doc): doc is T => doc !== null)
    })
  })

  /**
   * DERIVED ATOM: Map of all docs by ID for fast lookups
   * Read-only. Automatically recomputes when allDocsAtom changes.
   */
  const docsMapAtom = atom((get) => {
    const allDocs = get(allDocsAtom)
    const map = new Map<Uuid, T>()

    allDocs.forEach((doc) => {
      if (doc?.id) {
        map.set(doc.id, doc)
      }
    })

    // console.log(`${logPrefix} 🗺️ Recomputed docsMap, size: ${map.size}`)
    return map
  })
  docsMapAtom.debugLabel = `${docType}:map`

  /**
   * ATOM FAMILY: Filtered document IDs by query
   * Each unique query gets its own atom that stores ONLY IDs
   */
  const filteredDocIdsAtom = atomFamily((queryKey: string) => {
    const baseAtom = atom<Uuid[]>([])
    baseAtom.debugLabel = `${docType}:filteredIds:${queryKey.slice(0, 50)}`

    // console.log(
    //   `${logPrefix} 🔍 Created filteredDocIdsAtom for query: ${queryKey.slice(0, 100)}`
    // )
    return baseAtom
  })

  /**
   * DERIVED ATOM FAMILY: Filtered documents (maps IDs to actual docs)
   * Reads from filteredDocIdsAtom and maps each ID to its document
   */
  const filteredDocsAtom = atomFamily((queryKey: string) => {
    return atom((get) => {
      const ids = get(filteredDocIdsAtom(queryKey))
      return ids
        .map((id) => get(docByIdAtom(id)))
        .filter((doc): doc is T => doc !== null)
    })
  })

  /**
   * HELPER: Get docs by multiple IDs
   * Derived atom that pulls from individual doc atoms
   */
  const docsByIdsAtom = atomFamily((ids: Uuid[]) => {
    return atom((get) => {
      const docs = ids
        .map((id) => get(docByIdAtom(id)))
        .filter((doc): doc is T => doc !== null)

      // console.log(
      //   `${logPrefix} 📦 Read docsByIds, requested: ${ids.length}, found: ${docs.length}`
      // )
      return docs
    })
  })

  /**
   * WRITABLE ATOM: Update multiple docs atomically
   * This allows batch updates to docByIdAtom from outside React components.
   * Also updates allLoadedIdsAtom to track which IDs have been loaded.
   */
  const updateDocsAtom = atom(
    null,
    (_get, set, updates: Array<{ id: Uuid; doc: T | null }>) => {
      updates.forEach(({ id, doc }) => {
        set(docByIdAtom(id), doc)
      })

      // Update master list of loaded IDs
      set(allLoadedIdsAtom, (prevSet: Set<Uuid>) => {
        const newSet = new Set<Uuid>(prevSet)
        updates.forEach(({ id, doc }) => {
          if (doc !== null) {
            newSet.add(id)
          } else {
            // Optionally remove ID when doc is null (deleted)
            // For now, we keep it in the set to track "we tried to load this"
            // newSet.delete(id)
          }
        })
        return newSet
      })

      // console.log(
      //   `${logPrefix} 📝 Batch updated ${updates.length} docs atomically`
      // )
    }
  )
  updateDocsAtom.debugLabel = `${docType}:updateDocs`

  return {
    // Core atoms (writable)
    docByIdAtom, // THE source of truth for each document (single base for all fetch methods)
    allLoadedIdsAtom, // Master list of all loaded IDs (regardless of fetch method)
    allDocIdsAtom, // IDs from useAllDocs
    docIdsForBatchAtom, // IDs from useSomeDocs
    filteredDocIdsAtom, // IDs from useFilteredDocs
    updateDocsAtom, // Batch update multiple docs

    // Derived atoms (read-only, computed from core atoms)
    allDocsAtom, // Maps allDocIdsAtom → docs
    docsBatchAtom, // Maps docIdsForBatchAtom → docs
    filteredDocsAtom, // Maps filteredDocIdsAtom → docs
    docsMapAtom, // Maps allDocsAtom → Map<id, doc>
    docsByIdsAtom, // Maps array of IDs → docs

    // Loading state atoms
    ...loadingAtoms,

    logPrefix,
  }
}

export type DocAtoms<T extends DataWithId> = ReturnType<
  typeof createDocAtoms<T>
>
